/**
 * Date labels on a chart axis.
 *
 * The AI writes x values the way it would say them: "1 Oct", "13 Oct (partial to 11:35)",
 * "1 October 2026", "Oct 1, 2026", "01/10/2026", "2026-10-01". Only the ISO form used to be
 * recognised as a date; every other label sorted as text, so 1 Oct was followed by 10, 11 and
 * 12 Oct before 2 Oct. parseDateLabel reads the date a label starts with, and dateLabelKeys
 * gives one sort key per label when every label carries a date (null otherwise, so the caller
 * keeps its text sort).
 *
 * Numeric day/month forms are read day-first (UK), the way the answers are written. Labels
 * without a year borrow the first year another label states, so "1 Oct" and "13 Oct 2026"
 * still order as days of the same month; with no year anywhere, a leap year stands in.
 *
 * The server twin is Business/Reports/Raster/ChartDateLabel.cs: keep the two in step.
 */

const MONTHS = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

// "Mon ", "Monday, " before the date.
const WEEKDAY_PREFIX = /^(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)[a-z]*\.?,?\s+/i;

// 2026-10-13, 2026-10-13T11:35, 2026-10-13 11:35
const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2}))?/;
// 1 Oct, 1st October 2026, 1 Oct 2026 11:35
const DAY_MONTH = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]{3,9})\.?,?(?:\s+(\d{4}))?(?:,?\s+(\d{1,2}):(\d{2}))?/i;
// Oct 1, October 1st 2026, Oct 1, 2026 11:35
const MONTH_DAY = /^([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?(?:\s+(\d{4}))?(?:,?\s+(\d{1,2}):(\d{2}))?/i;
// 01/10, 1/10/26, 01/10/2026, 01.10.2026, 01-10-2026 (day first; the dotted and dashed forms need the year)
const NUMERIC = /^(\d{1,2})(?:\/(\d{1,2})(?:\/(\d{4}|\d{2}))?|[.-](\d{1,2})[.-](\d{4}|\d{2}))(?:,?\s+(\d{1,2}):(\d{2}))?/;

// What follows the date may be anything that does not continue a word or a number:
// " (partial to 11:35)", ", week 41", "" — but not "ober" or "5".
const endsCleanly = (text, matched) => {
  const rest = text.slice(matched.length);
  return rest === "" || !/^[a-z0-9]/i.test(rest);
};

const toYear = (text) => {
  if (text === undefined) return undefined;
  const year = parseInt(text, 10);
  return text.length === 2 ? 2000 + year : year;
};

const build = (year, month, day, hours, minutes) => {
  if (month < 0 || month > 11 || day < 1 || day > 31) return null;
  const h = hours === undefined ? 0 : parseInt(hours, 10);
  const m = minutes === undefined ? 0 : parseInt(minutes, 10);
  if (h > 23 || m > 59) return null;
  return { year, month, day, hours: h, minutes: m };
};

/**
 * The date a label starts with, or null when it does not start with one.
 * @returns {{year: number|undefined, month: number, day: number, hours: number, minutes: number}|null}
 *   month is 0-based; year is undefined when the label does not state one.
 */
export function parseDateLabel(value) {
  if (typeof value !== "string") return null;
  let text = value.trim();
  const weekday = text.match(WEEKDAY_PREFIX);
  if (weekday) text = text.slice(weekday[0].length);
  if (text === "") return null;

  let match = text.match(ISO);
  if (match && endsCleanly(text, match[0])) {
    return build(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10), match[4], match[5]);
  }

  match = text.match(DAY_MONTH);
  if (match && endsCleanly(text, match[0])) {
    const month = MONTHS[match[2].toLowerCase()];
    if (month === undefined) return null;
    return build(toYear(match[3]), month, parseInt(match[1], 10), match[4], match[5]);
  }

  match = text.match(MONTH_DAY);
  if (match && endsCleanly(text, match[0])) {
    const month = MONTHS[match[1].toLowerCase()];
    if (month === undefined) return null;
    return build(toYear(match[3]), month, parseInt(match[2], 10), match[4], match[5]);
  }

  match = text.match(NUMERIC);
  if (match && endsCleanly(text, match[0])) {
    const month = parseInt(match[2] ?? match[4], 10) - 1;
    return build(toYear(match[3] ?? match[5]), month, parseInt(match[1], 10), match[6], match[7]);
  }

  return null;
}

const FALLBACK_YEAR = 2000; // a leap year, so 29 Feb survives

/**
 * One sort key (ms since the epoch, UTC) per label, in the labels' order — or null when any
 * label does not start with a date, in which case the axis is not a date axis.
 */
export function dateLabelKeys(values) {
  const parsed = [];
  for (const value of values) {
    const date = parseDateLabel(value);
    if (!date) return null;
    parsed.push(date);
  }
  const statedYear = parsed.find((date) => date.year !== undefined)?.year;
  const fillYear = statedYear === undefined ? FALLBACK_YEAR : statedYear;
  return parsed.map((date) =>
    Date.UTC(date.year === undefined ? fillYear : date.year, date.month, date.day, date.hours, date.minutes)
  );
}
